import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Menu, X } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import FeedbackButton from "@/components/FeedbackButton";
import DisclaimerGate from "@/components/DisclaimerGate";
import PWAInstall from "@/components/PWAInstall";
import LanguagePicker from "@/components/LanguagePicker";
import { identifyUser } from "@/lib/analytics";
import { BRAND } from "@/lib/brand";

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => { if (user) identifyUser(user); }, [user]);
    useEffect(() => { setMenuOpen(false); }, [location.pathname]);

    const handleLogout = () => { logout(); navigate("/"); };

    const linkClass = ({ isActive }) =>
        `text-sm font-body tracking-wide transition-colors ${
            isActive ? "text-terracotta" : "text-inkSoft hover:text-ink"
        }`;

    const mobileLinkClass = ({ isActive }) =>
        `block w-full px-4 py-3 rounded-2xl font-body text-base ${
            isActive ? "bg-terracotta/10 text-terracotta" : "text-ink hover:bg-clay"
        }`;

    const displayName = user?.preferred_name || user?.name;

    return (
        <div className="relative z-10 min-h-screen flex flex-col">
            <header className="px-4 sm:px-6 md:px-12 py-4 md:py-5 flex items-center justify-between">
                <Link to="/companion" className="flex items-center gap-2.5" data-testid="brand-link">
                    <img src={BRAND.logo} alt={BRAND.fullName} className="w-7 h-7 object-contain" data-testid="brand-logo" />
                    <span className="font-heading text-2xl tracking-tight text-ink">{BRAND.handle}</span>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden md:flex items-center gap-5 lg:gap-7">
                    <NavLink to="/companion" end className={linkClass} data-testid="nav-companion">companion</NavLink>
                    <NavLink to="/journal" className={linkClass} data-testid="nav-journal">journal</NavLink>
                    <NavLink to="/memories" className={linkClass} data-testid="nav-memories">memories</NavLink>
                    <NavLink to="/reflection" className={linkClass} data-testid="nav-reflection">reflection</NavLink>
                    <NavLink to="/history" className={linkClass} data-testid="nav-history">history</NavLink>
                    <NavLink to="/invite" className={linkClass} data-testid="nav-invite">invite</NavLink>
                    <NavLink to="/settings" className={linkClass} data-testid="nav-settings">settings</NavLink>
                    <LanguagePicker />
                    <NotificationBell />
                    <div className="flex items-center gap-3 pl-3 border-l border-clayHover">
                        <span className="hidden lg:inline text-sm text-inkSoft font-body">{displayName}</span>
                        <button
                            data-testid="logout-btn"
                            onClick={handleLogout}
                            className="text-inkSoft hover:text-terracotta transition-colors"
                            aria-label="Sign out"
                        >
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </nav>

                {/* Mobile menu button */}
                <div className="md:hidden flex items-center gap-3">
                    <NotificationBell />
                    <button
                        onClick={() => setMenuOpen(!menuOpen)}
                        data-testid="mobile-menu-toggle"
                        className="w-9 h-9 rounded-full bg-clay flex items-center justify-center text-ink"
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                    >
                        {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </header>

            {/* Mobile drawer */}
            {menuOpen && (
                <nav className="md:hidden px-4 pb-6 border-b border-clayHover space-y-1" data-testid="mobile-nav">
                    <NavLink to="/companion" end className={mobileLinkClass}>companion</NavLink>
                    <NavLink to="/journal" className={mobileLinkClass}>journal</NavLink>
                    <NavLink to="/memories" className={mobileLinkClass}>memories</NavLink>
                    <NavLink to="/reflection" className={mobileLinkClass}>reflection</NavLink>
                    <NavLink to="/history" className={mobileLinkClass}>history</NavLink>
                    <NavLink to="/invite" className={mobileLinkClass}>invite</NavLink>
                    <NavLink to="/settings" className={mobileLinkClass}>settings</NavLink>
                    <div className="flex items-center justify-between px-4 py-3">
                        <LanguagePicker />
                        <button
                            onClick={handleLogout}
                            className="text-inkSoft hover:text-terracotta text-sm font-body flex items-center gap-1.5"
                        >
                            <LogOut className="w-4 h-4" /> sign out
                        </button>
                    </div>
                </nav>
            )}

            <main className="flex-1 px-4 sm:px-6 md:px-12 pb-20 md:pb-12">{children}</main>
            <DisclaimerGate />
            <FeedbackButton />
            <PWAInstall />
        </div>
    );
}
